/**
 * COMPOSANT UI: RichTextEditor
 * Éditeur de texte riche basé sur Tiptap.
 *
 * Props optionnelles :
 *   tagCategories — catégories de tags à proposer dans la barre d'outils.
 *                   Si absent, le bouton "Tag" est masqué.
 */

import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Link2,
  Heading2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Tag,
  Square,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { EmailButton } from './emailButtonExtension';
import type { EmailTagCategory } from '@/lib/emailTags';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  error?: string;
  /** Catégories de tags à proposer. Si absent, le bouton Tag est masqué. */
  tagCategories?: EmailTagCategory[];
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Écrivez votre message...',
  className,
  minHeight = '200px',
  error,
  tagCategories,
}: RichTextEditorProps) {
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [pendingTag, setPendingTag] = useState<{ tag: string; label: string } | null>(null);
  const [pendingDefault, setPendingDefault] = useState('');
  const tagDropdownRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          style: 'color: #ea1f3d; text-decoration: underline;',
        },
      }),
      EmailButton,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right'],
        defaultAlignment: 'left',
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none px-4 py-3 min-h-[200px]',
      },
    },
    editable: true,
    autofocus: false,
  });

  // Sync external value changes (e.g. template selection)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  // Fermer le dropdown tags au clic extérieur
  useEffect(() => {
    if (!tagDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        setTagDropdownOpen(false);
        setPendingTag(null);
        setPendingDefault('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tagDropdownOpen, setPendingTag, setPendingDefault]);

  if (!editor) return null;

  // --- Actions de la barre d'outils ---

  const toggleLink = () => {
    if (editor.isActive('link')) {
      const previousUrl = editor.getAttributes('link').href;
      const url = window.prompt('URL du lien (laisser vide pour supprimer):', previousUrl);
      if (url === null) return;
      if (url === '') {
        editor.chain().focus().unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    } else {
      const url = window.prompt('URL du lien:');
      if (!url) return;
      // Retire un éventuel mark bouton : lien + bouton sur le même texte
      // produiraient des <a> imbriqués dans le HTML envoyé.
      editor.chain().focus().unsetMark('emailButton').setLink({ href: url }).run();
    }
  };

  const insertEmailButton = () => {
    const text = window.prompt('Texte du bouton:');
    if (!text) return;
    const url = window.prompt('URL du bouton:');
    if (!url) return;
    editor.commands.insertEmailButton({ href: url, text });
  };

  const insertTag = (tag: string, defaultValue?: string) => {
    const finalTag = defaultValue?.trim()
      ? tag.slice(0, -2) + '|' + defaultValue.trim() + '}}'
      : tag;
    editor.chain().focus().insertContent(finalTag).run();
    setTagDropdownOpen(false);
    setPendingTag(null);
    setPendingDefault('');
  };

  // --- Composants internes ---

  const MenuButton = ({
    onClick,
    active,
    children,
    title,
  }: {
    onClick: () => void;
    active?: boolean;
    children: React.ReactNode;
    title: string;
  }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={cn('p-2 rounded hover:bg-zinc-200 transition-colors', active && 'bg-zinc-300')}
      title={title}
      tabIndex={-1}
    >
      {children}
    </button>
  );

  return (
    <div className={cn('border border-zinc-300 rounded-md', className)}>
      {/* Barre d'outils */}
      <div className="flex flex-wrap gap-1 p-2 bg-zinc-50 border-b border-zinc-200 rounded-t-md">
        {/* Mise en forme texte */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Gras (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italique (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Souligné (Ctrl+U)"
        >
          <UnderlineIcon className="h-4 w-4" />
        </MenuButton>

        <div className="w-px bg-zinc-300 mx-1" />

        {/* Titre */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Titre"
        >
          <Heading2 className="h-4 w-4" />
        </MenuButton>

        <div className="w-px bg-zinc-300 mx-1" />

        {/* Listes */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Liste à puces"
        >
          <List className="h-4 w-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Liste numérotée"
        >
          <ListOrdered className="h-4 w-4" />
        </MenuButton>

        <div className="w-px bg-zinc-300 mx-1" />

        {/* Citation */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="Citation"
        >
          <Quote className="h-4 w-4" />
        </MenuButton>

        {/* Lien */}
        <MenuButton onClick={toggleLink} active={editor.isActive('link')} title="Insérer un lien">
          <Link2 className="h-4 w-4" />
        </MenuButton>

        {/* Bouton lien (email CTA) */}
        <MenuButton
          onClick={insertEmailButton}
          active={editor.isActive('emailButton')}
          title="Insérer un bouton (lien stylé)"
        >
          <Square className="h-4 w-4" />
        </MenuButton>

        <div className="w-px bg-zinc-300 mx-1" />

        {/* Alignement */}
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          title="Aligner à gauche"
        >
          <AlignLeft className="h-4 w-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          title="Centrer"
        >
          <AlignCenter className="h-4 w-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          title="Aligner à droite"
        >
          <AlignRight className="h-4 w-4" />
        </MenuButton>

        <div className="w-px bg-zinc-300 mx-1" />

        {/* Annuler / Refaire */}
        <MenuButton onClick={() => editor.chain().focus().undo().run()} title="Annuler (Ctrl+Z)">
          <Undo className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().redo().run()} title="Refaire (Ctrl+Y)">
          <Redo className="h-4 w-4" />
        </MenuButton>

        {/* Bouton Tag — affiché uniquement si des catégories sont fournies */}
        {tagCategories && tagCategories.length > 0 && (
          <>
            <div className="w-px bg-zinc-300 mx-1" />
            <div className="relative" ref={tagDropdownRef}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setTagDropdownOpen((open) => !open);
                  setPendingTag(null);
                  setPendingDefault('');
                }}
                className={cn(
                  'flex items-center gap-1 px-2 py-1.5 rounded text-xs font-medium hover:bg-zinc-200 transition-colors',
                  tagDropdownOpen && 'bg-zinc-300'
                )}
                title="Insérer un tag dynamique"
                tabIndex={-1}
              >
                <Tag className="h-3.5 w-3.5" />
                <span>Tag</span>
              </button>

              {tagDropdownOpen && (
                <div
                  className="absolute left-0 top-full mt-1 z-50 bg-white border border-zinc-200 rounded-lg shadow-lg w-72"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {pendingTag ? (
                    <div className="p-3 flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setPendingTag(null);
                            setPendingDefault('');
                          }}
                          className="text-zinc-500 hover:text-zinc-800 text-xs flex items-center gap-1"
                        >
                          ← Retour
                        </button>
                        <span className="text-xs font-medium text-zinc-700 truncate">
                          {pendingTag.label}
                        </span>
                      </div>
                      <code className="text-xs bg-zinc-100 rounded px-2 py-1 text-zinc-600 break-all">
                        {pendingTag.tag}
                      </code>
                      <div>
                        <label className="block text-xs text-zinc-600 mb-1">
                          Valeur par défaut <span className="text-zinc-400">(optionnel)</span>
                        </label>
                        <input
                          type="text"
                          autoFocus
                          value={pendingDefault}
                          onChange={(e) => setPendingDefault(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              insertTag(pendingTag.tag, pendingDefault);
                            }
                            if (e.key === 'Escape') {
                              setPendingTag(null);
                              setPendingDefault('');
                            }
                          }}
                          placeholder="Ex : À venir"
                          className="w-full px-2 py-1.5 text-xs border border-zinc-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          insertTag(pendingTag.tag, pendingDefault);
                        }}
                        className="w-full px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors"
                      >
                        Insérer
                      </button>
                    </div>
                  ) : (
                    <div className="p-2 max-h-80 overflow-y-auto">
                      <p className="text-xs text-zinc-500 px-2 pb-2 border-b border-zinc-100">
                        Cliquez sur un tag pour l'insérer.
                      </p>
                      {tagCategories.map((category) => (
                        <div key={category.id} className="mt-2">
                          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide px-2 mb-1">
                            {category.label}
                          </p>
                          {category.note && (
                            <p className="text-xs text-amber-600 px-2 mb-1 italic">
                              {category.note}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-1 px-1">
                            {category.tags.map((t) => (
                              <button
                                key={t.tag}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setPendingTag(t);
                                  setPendingDefault('');
                                }}
                                className="px-2 py-0.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-xs transition-colors border border-blue-200"
                                title={t.tag}
                              >
                                {t.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Zone d'édition */}
      <div style={{ minHeight }} className={cn('bg-white', !error && 'rounded-b-md')}>
        <EditorContent editor={editor} />
      </div>

      {/* Message d'erreur */}
      {error && <p className="text-sm text-red-600 px-4 py-2 bg-red-50 rounded-b-md">{error}</p>}
    </div>
  );
}
